import { InvalidSortDirectionError } from '../errors';

export type SortComparer = (left: string | number, right: string | number) => number;

export enum DiffTypeBasic {
  leftDiff = 'Unique Items From First Array',
  rightDiff = 'Unique Items From Second Array',
  symmetricDiff = 'Unique Items From Both Arrays'
}

export enum SortDirection {
  asc = 'asc',
  desc = 'desc'
}

export enum SortDirectionBasic {
  ascending = 'Ascending',
  descending = 'Descending'
}

export enum ReducingOperation {
  avg = 'avg',
  max = 'max',
  min = 'min',
  sum = 'sum'
}

export enum ReducingOperationBasic {
  averaging = 'Averaging',
  findingMaximum = 'Finding the Maximum',
  findingMinimum = 'Finding the Minimum',
  summing = 'Summing'
}

/**
 * Factory for a function that will compare two values and return 1 if the left value is greater than the right, 0
 * if the values are equivalent, or -1 if the left value is less than the right.
 * @param direction The sort direction.
 * @param localeLanguage The locale language to use when comparing values.
 *
 * @returns A function that will compare two values and return a numeric comparison result.
 */
export function createSortComparer<S extends `${SortDirection}`, SB extends `${SortDirectionBasic}`>(
  direction: SortDirection | SortDirectionBasic | S | SB = SortDirection.asc,
  localeLanguage: string
): SortComparer {
  let reverseSort = false;
  switch (direction) {
    case SortDirection.asc:
    case SortDirectionBasic.ascending: {
      break;
    }
    case SortDirection.desc:
    case SortDirectionBasic.descending: {
      reverseSort = true;
      break;
    }
    default:
      throw new InvalidSortDirectionError();
  }

  return (left: string | number, right: string | number): number => {
    if (reverseSort) {
      const temp = left;
      left = right;
      right = temp;
    }

    if (typeof left === 'string' && typeof right === 'string') {
      return left?.localeCompare(right, localeLanguage, { ignorePunctuation: true });
    }

    return left > right ? 1 : left < right ? -1 : 0;
  };
}
